import React from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { MessageCircle, ChevronRight, Spade, Dice5, Plane, CircleDot, Gem, Zap, ShieldCheck, Wallet, BadgePercent } from "lucide-react";
import { SITE, GAMES } from "../data/mock";

const CATEGORIES = [
  { icon: Spade, title: "Card Games", desc: "Classic Indian & international card games with live dealers.", games: ["Teen Patti", "Andar Bahar", "Dragon Tiger", "Baccarat", "Blackjack", "Poker", "32 Cards", "Hi-Lo"] },
  { icon: CircleDot, title: "Roulette & Wheel", desc: "Real-time spinning action with big multipliers.", games: ["Lightning Roulette", "Auto Roulette", "Crazy Time", "Dream Catcher", "Monopoly Live", "Mega Wheel"] },
  { icon: Plane, title: "Crash & Instant", desc: "Fast-paced games for quick wins, cash out anytime.", games: ["Aviator", "JetX", "Crash", "Mines", "Plinko", "Dice", "Limbo", "Coin Flip"] },
  { icon: Gem, title: "Slots", desc: "650+ premium slots from top studios.", games: ["Gates of Olympus", "Sweet Bonanza", "Book of Dead", "Starburst", "Wolf Gold", "Big Bass Bonanza"] },
  { icon: Dice5, title: "Live Casino", desc: "HD live studios with professional dealers, 24x7.", games: ["Live Teen Patti", "Live Roulette", "Live Baccarat", "Sic Bo", "Lucky 7", "Bet on Numbers"] },
];

const PROVIDERS = ["Evolution", "Ezugi", "Pragmatic Play", "Spribe", "Betsoft", "Playtech", "Ezugi Live", "Supernowa"];

const PERKS = [
  { icon: Zap, t: "Instant Casino ID", d: "2 minute me WhatsApp par ID activate. Turant khelna shuru." },
  { icon: BadgePercent, t: "5% Welcome Bonus", d: "Pehle deposit par bonus + 10% weekly loss commission." },
  { icon: Wallet, t: "Fastest Withdrawal", d: "Casino winnings 3-5 min me UPI/Bank par." },
  { icon: ShieldCheck, t: "100% Safe & Fair", d: "Licensed providers, SSL encrypted, provably fair games." },
];

export default function Casino() {
  return (
    <div data-testid="casino-page">
      <SEO
        title="Online Casino ID | Teen Patti, Roulette, Aviator & 650+ Games - OnlineCricketID.guru"
        description="Get an online casino ID in 2 minutes. Play Teen Patti, Andar Bahar, Lightning Roulette, Aviator, Dragon Tiger & 650+ live casino games with instant withdrawal on OnlineCricketID.guru."
        keywords="online casino id, teen patti online, andar bahar, live casino india, aviator game, dragon tiger, lightning roulette, online cricket id casino"
      />
      <PageHero
        title="Live Casino & 650+ Games"
        breadcrumb="Casino"
        subtitle="Teen Patti, Andar Bahar, Roulette, Aviator aur 650+ live casino games - ek hi ID par. Instant activation, 5% bonus aur fastest withdrawal ke saath."
      />

      {/* POPULAR GAMES */}
      <section className="section-pad" data-testid="casino-popular">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white">Most Popular Games</h2>
            <p className="mt-3 text-slate-400">India ke sabse trending casino games, live dealers ke saath 24x7 available.</p>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
            {GAMES.map((g) => (
              <a
                key={g.name}
                href={SITE.whatsappUrl}
                target="_blank"
                rel="noreferrer"
                data-testid={`casino-game-${g.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative rounded-2xl overflow-hidden border border-amber-500/20 aspect-square bg-[#0c1530] shadow-lg shadow-black/30 hover:border-amber-500/50 transition-colors"
              >
                <img src={g.img} alt={g.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1226] via-[#0a1226]/30 to-transparent" />
                <span className="absolute top-3 left-3 text-[10px] font-semibold uppercase tracking-wide bg-black/55 backdrop-blur-md text-amber-300 border border-amber-500/30 rounded-full px-2.5 py-1">{g.tag}</span>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-base sm:text-lg font-extrabold text-white leading-tight">{g.name}</h3>
                  <span className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-amber-300 translate-y-1 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                    <MessageCircle className="w-3.5 h-3.5" /> Play Now <ChevronRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="section-pad bg-gradient-to-b from-transparent via-[#0c1530]/40 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white">Game Categories</h2>
            <p className="mt-3 text-slate-400">Har mood ke liye kuch na kuch - cards se leke crash games tak.</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CATEGORIES.map((c) => (
              <div key={c.title} className="card-dark p-6" data-testid={`casino-category-${c.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/25 to-amber-400/10 border border-amber-500/30 flex items-center justify-center">
                  <c.icon className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-white">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {c.games.map((g) => (
                    <span key={g} className="text-xs text-slate-300 bg-white/5 border border-white/10 rounded-full px-3 py-1">{g}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white">Why Play Casino on OnlineCricketID</h2>
            <p className="mt-3 text-slate-400">Ek hi ID, 650+ games aur India ki best payout speed.</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PERKS.map((p) => (
              <div key={p.t} className="card-dark p-6">
                <div className="w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center"><p.icon className="w-6 h-6 text-amber-400" /></div>
                <h3 className="mt-4 text-lg font-bold text-white">{p.t}</h3>
                <p className="mt-2 text-sm text-slate-400">{p.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-slate-400 text-sm uppercase tracking-widest">Powered by top providers</p>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              {PROVIDERS.map((pr) => (
                <span key={pr} className="text-sm font-semibold text-amber-200/90 bg-white/5 border border-amber-500/15 rounded-lg px-4 py-2">{pr}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-[#15244a] via-[#0f1a36] to-[#0c1530] border border-amber-500/25 p-8 md:p-12 relative overflow-hidden text-center">
            <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-amber-500/15 blur-3xl" />
            <div className="relative">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white leading-tight">Apni Casino ID Abhi Banao</h2>
              <p className="mt-3 text-slate-300 max-w-xl mx-auto">2 minute me WhatsApp par ID lo aur 650+ games khelna shuru karo - 5% welcome bonus ke saath.</p>
              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                <a href={SITE.whatsappUrl} target="_blank" rel="noreferrer" className="btn-gold inline-flex items-center gap-2 px-6 py-3.5 rounded-xl" data-testid="casino-cta-whatsapp">
                  <MessageCircle className="w-5 h-5" /> Get Casino ID on WhatsApp
                </a>
                <Link to="/how-to-play" className="btn-outline-gold inline-flex items-center gap-1.5 px-5 py-3 rounded-xl">
                  How to Play <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
